import React, { FC, ReactElement, useEffect } from 'react';
import { connect } from 'react-redux';
import useConfig from '../../hooks/useConfig';
import { CategoriesState, NormalizedEntitiesAndUrlKeys } from '../../redux/category/types';
import StateInterface, { BellaThunkDispatch } from '../../redux/types';
import {
    CrossCategoryNavigation as CrossCatNavInterface,
    ListingPageUrlState,
    ProductFilters,
    ProductsState,
} from '../../redux/products/types';
import { showOverlay as showOverlayAction } from '../../redux/overlays/actions';
import { OVERLAY_ID as FILTERS_OVERLAY_ID } from '../Overlays/FiltersFlyout/index';
import useListingPageUrlState from '../../hooks/ListingPage/useListingPageUrlState';
import { PageType } from '../../hooks/UrlManager/types';
import StatusCodeComponentWrapper from '../../hooks/StaticContent/StatusCodeComponentWrapper';
import { computeSeoIndexableAndFollow } from '../../hooks/ListingPage/seo/seoUtils';
import NotFound from '../NotFound';
import { LinkedBanners, ROBOTS_INDEX_CONTENT, SeoState } from '../../redux/seo/types';
import { ShopToClubBannerABTestState } from '../../redux/shopToClubBanner/types';
import useListingPageTitle from '../../hooks/ListingPage/useListingPageTitle';
import useImageFormatter from '../../hooks/useImageFormatter';
import useBreadcrumbsState from '../../hooks/useBreadcrumbsState';
import { ProductList } from '../../ga/types';
import { FormattedInfusionData } from '../../redux/infusions/types';
import { SHOP_TO_CLUB_FOOTER_LINK } from '../../redux/cms/constants';
import ErrorBoundary from '../Generic/ErrorBoundaries';
import FilterSeoLinks from '../SeoLinks/FilterSeoLinks';
import { NavigationBaseData } from '../Generic/NavigationItem';
import BackButton from './BackButton';
import Grid from './ProductGrid/index';
import { StyledCategoryNavContainer } from './SideCategoryNavigation';
import SideNavigation from './SideCategoryNavigation/SideNavigation';
import FilterAndSortBox from './FilterAndSortBox/index';
import PageTitle from './Header/PageTitle';
import PageHeader from './Header/StyledPageHeader';
import Pagination from './Pagination/index';
import RegularTitle from './Header/RegularTitle';
import DidYouMeanTitle from './Header/DidYouMeanTitle';
import BreadCrumbs from './BreadCrumbs';
import AppliedFiltersSliderContainer from './AppliedFilters';
import Banners from './Banners/index';
import { getNavigationForCategory } from './CrossCategoryNavigation/crossCategoryNavigationService';
import ShopToClubBanner from './Banners/ShopToClubBannerTemp';
import CategoryFilterBucket from './FilterAndSortBox/CategoryFilterBucket';
import ListingPageSeoHead from './Seo/SeoHead';
import CrossCategoryNavigation from './CrossCategoryNavigation';
import getCategoryPageBackOptions from './backOptions';
import shouldRenderSeoText, { SeoTextTop, SeoTextBottom } from './Seo';
import {
    SideBar,
    StyleGridWrapper,
    ListingPageContentWrapper,
    ListingPageContainer,
    SeoTextTopWrapper,
    SeoTextBottomWrapper,
    MainContent,
    BreadCrumbsWrapper,
} from './ListingPageElements';
import SkuDiverProvider from './SkuDiver/SkuDiverContext';
import SeoSlider from './SeoSlider';
import ListingPageBubbleSlider from './ListingPageBubbleSlider';

const OPEN_GRAPH_IMAGE = {
    height: '215',
    width: '259',
    placeholder: 'https://static.westwingnow.de/ww-shop/assets/images/westwing-now-logo.svg',
};

interface ReduxStateProps {
    crossCategoryNavigation: CrossCatNavInterface;
    products: ProductsState;
    infusions: FormattedInfusionData | undefined;
    categories: CategoriesState;
    didInvalidate: boolean;
    totalCount: number;
    seoData: SeoState;
    shopToClubFooterBanners: LinkedBanners[];
    filters: ProductFilters[];
    shopToClubABTest: ShopToClubBannerABTestState;
}

interface ListingPageDispatchProps {
    showOverlay: (overlayId: string) => void;
    dispatchSeoIndexableContent: (urlState: ListingPageUrlState) => void;
}

export interface ListingPageProps extends ReduxStateProps, ListingPageDispatchProps {}

// eslint-disable-next-line complexity
const ListingPage: FC<ListingPageProps> = (props): ReactElement => {
    const {
        crossCategoryNavigation: fallbackCrossCategoryNavigation,
        products,
        infusions,
        didInvalidate,
        totalCount,
        showOverlay,
        dispatchSeoIndexableContent,
        seoData: { seoData },
        categories,
        shopToClubFooterBanners,
        shopToClubABTest,
        filters,
    } = props;

    const [urlState] = useListingPageUrlState();
    const config = useConfig();
    const { listingPageKey, listingPageType } = urlState;

    const crossCategoryNavigation =
        listingPageType === PageType.CATEGORY
            ? getNavigationForCategory(listingPageKey, categories.normalized)
            : fallbackCrossCategoryNavigation;

    const [pageHeaderTitle, pageMetaTitle] = useListingPageTitle(urlState, seoData);
    const titleTextDescription = listingPageType === PageType.BRANDS ? seoData.text : '';

    useEffect(() => {
        dispatchSeoIndexableContent(urlState);
    }, [dispatchSeoIndexableContent, urlState]);

    const isSearch = listingPageType === PageType.ALLPRODUCTS;

    const handleFiltersClick = () => {
        showOverlay(FILTERS_OVERLAY_ID);
    };
    const firstProductImageUrl = useImageFormatter(
        (products.products[0] || {}).baseImage || OPEN_GRAPH_IMAGE.placeholder,
        {
            padding: false,
            height: OPEN_GRAPH_IMAGE.height,
            width: OPEN_GRAPH_IMAGE.width,
        }
    );

    const { entities = {}, urlKeys = {} } = categories.normalized || ({} as NormalizedEntitiesAndUrlKeys);
    const { breadcrumbList } = useBreadcrumbsState({ entities, urlKeys }, urlState, {
        pageTitle: pageMetaTitle,
        host: '/',
    });

    const isCategoryPage = listingPageType === PageType.CATEGORY;
    const [backLink, isRootLink, categoryId] = isCategoryPage
        ? getCategoryPageBackOptions(breadcrumbList, listingPageKey, categories.normalized)
        : [crossCategoryNavigation.parentCategory.link, crossCategoryNavigation.parentCategory.isRoot];

    const pageHasCategoryFilter = listingPageType !== PageType.CATEGORY;
    const { seoText = '', seoTextBottom = '', similarPages = [], seoSlider } = seoData;
    const bubbles: NavigationBaseData[] = similarPages.map(item => ({
        name: item.similar_page_text,
        link: item.similar_page_link,
        isSelected: false,
    }));

    const redirectOccurred = products.error && products.error.isRedirect;

    return (
        <>
            {didInvalidate && products.isFetchingComplete && !products.products.length && !redirectOccurred ? (
                <StatusCodeComponentWrapper code={404}>
                    <NotFound />
                </StatusCodeComponentWrapper>
            ) : (
                <ListingPageContainer>
                    <SkuDiverProvider>
                        <ListingPageContentWrapper>
                            <BreadCrumbsWrapper data-testid="bread_crumbs_wrapper">
                                {isCategoryPage ? <BreadCrumbs breadcrumbList={breadcrumbList} /> : ''}
                            </BreadCrumbsWrapper>
                            <SideBar>
                                {!pageHasCategoryFilter ? (
                                    <StyledCategoryNavContainer>
                                        <SideNavigation />
                                    </StyledCategoryNavContainer>
                                ) : (
                                    <CategoryFilterBucket filters={filters} />
                                )}
                            </SideBar>
                            {shouldRenderSeoText(urlState) && (
                                <>
                                    <SeoTextTopWrapper>
                                        <SeoTextTop rawHtml={seoText} />
                                    </SeoTextTopWrapper>
                                    <SeoTextBottomWrapper>
                                        <SeoTextBottom name={pageHeaderTitle} rawHtml={seoTextBottom} />
                                    </SeoTextBottomWrapper>
                                </>
                            )}
                            <MainContent>
                                <ListingPageSeoHead
                                    pageTitle=""
                                    urlState={urlState}
                                    seoData={seoData}
                                    config={config}
                                    totalCount={totalCount}
                                    entities={entities}
                                    urlKeys={urlKeys}
                                    openGraphImage={firstProductImageUrl}
                                />
                                <ErrorBoundary boundaryName="ListingPageHeader" fallbackComponent={null}>
                                    <Banners banners={seoData.linkedBanners} bannerTitle={pageHeaderTitle} />

                                    <PageHeader>
                                        <ErrorBoundary boundaryName="CrossCategoryNavBackBtn" fallbackComponent={null}>
                                            <BackButton to={backLink} isRoot={isRootLink} />
                                        </ErrorBoundary>

                                        <PageTitle>
                                            <RegularTitle
                                                pageTitle={pageHeaderTitle}
                                                totalCount={totalCount}
                                                isSearch={isSearch}
                                                isRoot={isRootLink}
                                                titleTextDescription={titleTextDescription}
                                            />
                                            <DidYouMeanTitle />
                                        </PageTitle>
                                        <ErrorBoundary boundaryName="CrossCategoryNav" fallbackComponent={null}>
                                            <CrossCategoryNavigation categories={crossCategoryNavigation.categories} />
                                        </ErrorBoundary>
                                    </PageHeader>
                                </ErrorBoundary>
                                <StyleGridWrapper>
                                    <FilterAndSortBox handleFiltersClick={handleFiltersClick} />
                                    <AppliedFiltersSliderContainer />
                                    <Grid
                                        products={products.products}
                                        list={ProductList.products}
                                        infusions={infusions}
                                    />
                                    <Pagination totalCount={totalCount} />
                                    <SeoSlider sliderData={seoSlider} />
                                    <ListingPageBubbleSlider bubbles={bubbles} />
                                    <FilterSeoLinks />
                                    <ShopToClubBanner
                                        shopToClubFooterBanners={shopToClubFooterBanners}
                                        categoryId={categoryId}
                                        shopToClubABTest={shopToClubABTest}
                                    />
                                </StyleGridWrapper>
                            </MainContent>
                        </ListingPageContentWrapper>
                    </SkuDiverProvider>
                </ListingPageContainer>
            )}
        </>
    );
};

const mapDispatchToProps = (dispatch: BellaThunkDispatch): ListingPageDispatchProps => ({
    showOverlay: (id: string) => dispatch(showOverlayAction(id)),
    dispatchSeoIndexableContent: urlState => {
        const robotsIndexableContent = computeSeoIndexableAndFollow(urlState);
        dispatch({ type: ROBOTS_INDEX_CONTENT, payload: robotsIndexableContent });
    },
});

const mapStateToProps = (state: StateInterface): ReduxStateProps => ({
    didInvalidate: state.products.didInvalidate,
    products: state.products,
    infusions: state.infusions.infusion,
    categories: state.categories,
    totalCount: state.products.totalCount,
    crossCategoryNavigation: state.products.crossCategoryNavigation,
    seoData: state.seoData,
    shopToClubFooterBanners: state.cms.linkedBanners.data[SHOP_TO_CLUB_FOOTER_LINK],
    shopToClubABTest: state.shopToClubABTest,
    filters: state.products.filters,
});

const connected = connect(mapStateToProps, mapDispatchToProps)(ListingPage);

export default connected;
